import React from 'react';
import { createPortal } from 'react-dom';
import { usePopperTooltip, Config } from 'react-popper-tooltip';
import 'react-popper-tooltip/dist/styles.css';
import styled from 'styled-components';
import { BodyText } from '../typography';
import { PrimaryButton, SecondaryButton } from '../button';
import { breakpoint, theme } from '../../theme';
import * as Icons from '../../assets';

export type TooltipProps = Config & {
  callToActionType?: CallToActionButtonType;
  callToActionLabel?: string;
  onCallToAction?: () => void;
  text: string;
  title?:
    | string
    | (React.ComponentProps<typeof BodyText> & {
        text?: string;
        icon?: keyof typeof Icons;
        color?: string;
      });
  controlledVisible?: boolean;
  portalRoot?: HTMLElement;
  setControlledVisible?: (state: boolean) => void;
  customTrigger?: React.ReactElement;
  style?: React.CSSProperties;
};
type CallToActionButtonType = 'primary' | 'secondary';
const Trigger = styled(Icons.InfoCircleIcon)`
  fill: ${theme.colors.system.systemBlue};
  margin-bottom: -1px;
  width: 14px;

  ${breakpoint('lg')`
    margin-bottom: -3px;
    width: 16px;
  `}
`;

const StyledTooltip = styled.div`
  border: 1px solid ${theme.colors.neutrals.solidGrays[6]};
  box-shadow: 0 2px 6px 1px ${theme.colors.neutrals.solidGrays[6]};
  max-width: 305px;
  min-width: 280px;
  padding: ${theme.spacings[4].rem};
  text-align: left;
  div:has(button) {
    margin-top: 8px;
  }
`;

export const Tooltip: React.FC<TooltipProps> = ({
  callToActionType,
  callToActionLabel,
  onCallToAction,
  text,
  title,
  controlledVisible,
  setControlledVisible,
  portalRoot,
  customTrigger = <Trigger />,
  style,
  ...props
}) => {
  const {
    getArrowProps,
    getTooltipProps,
    setTooltipRef,
    setTriggerRef,
    visible,
  } = usePopperTooltip({
    interactive: true,
    offset: [0, 11],
    trigger: 'click',
    visible: controlledVisible,
    onVisibleChange: setControlledVisible,
    ...props,
  });

  return (
    <>
      <span ref={setTriggerRef}>{customTrigger}</span>
      {visible &&
        createPortal(
          <StyledTooltip
            ref={setTooltipRef}
            {...getTooltipProps({
              className: 'tooltip-container',
              style,
            })}
          >
            <Title title={title} />
            <BodyText size="sm">{text}</BodyText>
            {callToActionLabel && onCallToAction && (
              <CallToAction
                type={callToActionType}
                label={callToActionLabel}
                onClick={onCallToAction}
              />
            )}
            <div {...getArrowProps({ className: 'tooltip-arrow' })} />
          </StyledTooltip>,
          portalRoot || document.body
        )}
    </>
  );
};

const CallToAction: React.FC<
  React.ComponentProps<typeof PrimaryButton> & {
    type?: CallToActionButtonType;
    label: string;
    onClick: () => void;
  }
> = ({ type = 'secondary', label, onClick, ...props }) => {
  let Button = SecondaryButton;
  switch (type) {
    case 'primary':
      Button = PrimaryButton;
      break;
    case 'secondary':
      Button = SecondaryButton;
      break;
  }

  return (
    <Button fullWidth={false} size="small" onClick={onClick} {...props}>
      {label}
    </Button>
  );
};

const Title: React.FC<Pick<TooltipProps, 'title'>> = ({ title }) => {
  if (!title) {
    return null;
  }
  if (typeof title === 'string') {
    return (
      <BodyText size="sm">
        <b>{title}</b>
      </BodyText>
    );
  }

  const { text, icon, color, ...props } = title;

  // eslint-disable-next-line import/namespace
  const Icon = icon && Icons[icon] ? Icons[icon] : null;

  return (
    <BodyText
      size="sm"
      color={color}
      style={{ display: 'inline-flex', gap: 8 }}
      {...props}
    >
      {Icon && <Icon height={20} fill={color} />}
      <b>{text}</b>
    </BodyText>
  );
};
